import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bell, Check, X, FileText, User, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const NOTIF_ICONS = {
  new_case_file: FileText,
  new_user: User
};

const NOTIF_COLORS = {
  new_case_file: "text-red-400 bg-red-400/10",
  new_user: "text-blue-400 bg-blue-400/10"
};

export default function AdminNotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API}/admin/notifications`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error("Error fetching admin notifications:", err);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await fetch(`${API}/admin/notifications`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error("Error fetching admin notification count:", err);
    }
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) fetchNotifications();
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/admin/notifications/${notificationId}/read`, {
        method: "PUT", headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      setLoading(true);
      await fetch(`${API}/admin/notifications/read-all`, {
        method: "PUT", headers: getAuthHeaders()
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success("All notifications marked as read");
    } catch (err) {
      toast.error("Failed to mark all as read");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        data-testid="admin-notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <Badge
            className="absolute -top-1 -right-1 h-5 min-w-[20px] flex items-center justify-center bg-red-500 text-white text-xs px-1 border-0"
            data-testid="admin-notification-count"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden"
          data-testid="admin-notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-card/95 backdrop-blur">
            <h3 className="font-heading text-base uppercase tracking-wider">Admin Alerts</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  disabled={loading}
                  className="text-xs h-7 px-2"
                  data-testid="admin-mark-all-read-btn"
                >
                  <Check className="w-3 h-3 mr-1" />
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No admin notifications</p>
              </div>
            ) : (
              notifications.slice(0, 10).map((notif) => {
                const Icon = NOTIF_ICONS[notif.type] || Bell;
                const colorClass = NOTIF_COLORS[notif.type] || "text-primary bg-primary/10";

                return (
                  <div
                    key={notif.notification_id}
                    className={`px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer ${
                      !notif.read ? "bg-primary/5" : ""
                    }`}
                    onClick={() => !notif.read && markAsRead(notif.notification_id)}
                    data-testid={`admin-notif-item-${notif.notification_id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`p-2 rounded-lg ${colorClass} flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-medium truncate">{notif.title}</h4>
                          {!notif.read && (
                            <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1.5" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.message}</p>
                        <span className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(notif.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <Link
              to="/admin/notifications"
              onClick={() => setIsOpen(false)}
              className="block px-4 py-3 text-center text-base text-primary hover:bg-muted/30 transition-colors border-t border-border"
              data-testid="admin-view-all-notifications"
            >
              View all notifications
              <ChevronRight className="w-4 h-4 inline ml-1" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
